<template>
  <v-container>
    <Loader v-if="loading"></Loader>
    <template v-else>
      <h1>Hubspot Integration</h1>
      <template>
        <span>Connection Status: </span>
        <span v-if="hubspotConnected" class="text-green">Connected</span>
        <span v-else class="text-red">Not Connected</span>
      </template>
      <hr class="header" />

      <button v-if="!hubspotConnected"
        class="default-btn blue"
        @click="connectToHubspot"
      >Connect to Hubspot</button>

      <v-card>
        <v-card-title>
          How it works
        </v-card-title>
        <v-card-text>
          <p>
            A hubspot app has been created to connect LendingWell with a user-provided Hubspot account. Once connected, this app will be able to access contacts and forms on the Hubspot account. It will require two things:

            <ul>
              <li>
                An admin to denote what account to connect with Hubspot. This can be done by clicking on the 'Connect to Hubspot' button, if that has not already been done.
              </li>
              <li>
                The input below titled 'Hubspot Form Id' should be provided the form id of the form on Hubspot that will be used for creating/updating contacts.
              </li>
            </ul>
          </p>

          <p>
            Integration is not necessary, and if it has not been set up will have no effect on the site. If it has been set it, it perform the following functions:
            <ul>
              <li>
                When a user signs up for LendingWell (not when manually created by an admin), the LendingWell server will send the user info to Hubspot using the provided form.
              </li>
              <li>
                When a user updates their contact info, or whether they wish to subscribe to marketing emails, the LendingWell server will send the updated information to Hubspot using the same provided form.
              </li>
            </ul>
          </p>
        </v-card-text>
      </v-card>

      <template v-if="hubspotConnected">
        <v-row>
          <v-col cols="6">
            <v-card>
              <v-card-text>
                <a
                  v-if="hubPortalId"
                  class="default-btn link"
                  target="_blank"
                  :href="'https://app.hubspot.com?portalId=' + hubPortalId"
                > > Go to linked hubspot account</a>

                <v-text-field
                  :value="hubPortalId"
                  label="Hubspot Portal Id"
                  class="mt-4"
                  hint="The hubspot portal this app has been connected to"
                  outlined dense
                  disabled persistent-hint
                ></v-text-field>

                <div class="input-group">
                  <v-text-field
                    v-model="hubFormId"
                    label="Hubspot Form Id"
                    hint="This is the form that the app will use to create/update contacts in Hubspot"
                    outlined dense
                    persistent-hint
                  ></v-text-field>

                  <button
                    class="default-btn blue"
                    @click="updateFormId"
                    :disabled="sendingFormId"
                  >Update Form Id</button>
                </div>

              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AdminHubspot',
  props: {
    code: String,
  },
  data: () => ({
    loading: true,
    hubspotConnected: false,
    hubspotConnectLink: '',
    hubPortalId: '',
    hubFormId: '',
    sendingFormId: false,
  }),
  computed: {
    ...mapGetters('feathers', ['apiHubspot']),
  },
  async created () {
    try {
      if (this.code) {
        const data = await this.apiHubspot.create({ code: this.code })
        this.hubspotConnected = true
        this.hubPortalId = data.portal_id
      } else {
        const data = await this.apiHubspot.find()
        this.hubspotConnected = !!data.access_token
        this.hubPortalId = data.portal_id
        this.hubFormId = data.form_id
      }

      this.hubspotConnectLink = await this.apiHubspot.get(null, {
        query: { type: 'authLink' },
      })
    } catch (error) {
      this.addAlert({
        type: 'error',
        message: `There was an error registering Hubspot data: ${error.message}`,
      })
    }

    this.loading = false
  },
  methods: {
    async connectToHubspot () {
      window.location = this.hubspotConnectLink
    },
    async updateFormId () {
      this.sendingFormId = true
      await this.apiHubspot.patch(null, { form_id: this.hubFormId })

      this.addAlert({ message: 'Successfully updated the form id!' })
      this.sendingFormId = false
    },
  },
}
</script>

<style lang="scss" scoped></style>
